// The AI layer (PHASED-PLAN decision 5, card X4-01). Every AI feature has a rules implementation that gives a useful
// result on its own, and an optional provider implementation (Anthropic, Haiku 5.5) that writes the wording better. The
// workspace decides which one may run: the AI data policy below is the only gate in front of a provider call. Nothing in
// this file is a collection; it holds the policy setting, the reply labels and the shapes both engines return.
import { z } from "zod";
import { ISO, ISO_DATE } from "./common.ts";

/** What the workspace allows: nothing, the built-in rules only, or rules plus the connected provider. */
export const AI_MODES = ["off", "rules", "provider"] as const;
export const AI_MODE = z.enum(AI_MODES);
export type AiMode = z.infer<typeof AI_MODE>;

/** The features the AI layer offers. A fixed set the product understands; which of them may reach the provider is data. */
export const AI_FEATURES = ["clientSummary", "preCallBrief", "replyClassify", "replyDraft", "proposalDraft", "callSummary", "meetingNotes", "leadAssist", "reportAsk", "agreementTerms", "handoffBrief", "staffing", "workflowSteps", "threadSummary", "rewrite"] as const;
export const AI_FEATURE = z.enum(AI_FEATURES);
export type AiFeature = z.infer<typeof AI_FEATURE>;

/** The key terms of an agreement read from its text: each with the sentence it came from, and the labels added on apply. */
export type AgreementTermsResult = { terms: { label: string; value: string; quote: string }[]; applied: string[] };

/** The model every provider call uses unless the workspace names another one. */
export const DEFAULT_AI_MODEL = "claude-haiku-5-5";

/**
 * The workspace's AI data policy (Settings, Integrations, AI). `mode` is the gate: "off" turns every AI feature off,
 * "rules" runs only the built-in rules (no data leaves the server), "provider" lets the features in `providerFeatures`
 * send their facts to the provider. `redact` masks email addresses and phone numbers before anything is sent. The two
 * limits stop spend and misuse: a provider call past the monthly token limit runs on the rules instead, and the MCP
 * endpoint refuses calls past its daily limit. 0 means no limit.
 */
export const AI_POLICY = z.strictObject({
  mode: AI_MODE,
  providerFeatures: z.array(AI_FEATURE).max(AI_FEATURES.length).refine((a) => new Set(a).size === a.length, "List each feature once."),
  redact: z.boolean(),
  model: z.string().trim().regex(/^claude-[a-z0-9][a-z0-9.-]{1,40}$/, "Use a Claude model id such as claude-haiku-5-5."),
  monthlyTokenLimit: z.number().int().min(0).max(2_000_000_000),
  mcpDailyLimit: z.number().int().min(0).max(1_000_000),
  /** The owner's data policy text, shown verbatim on the AI page. Stored only; never sent to the provider. Empty means none. */
  dataPolicy: z
    .string()
    .trim()
    .max(2000)
    .optional()
    .transform((text) => (text ? text : undefined)),
});
export type AiPolicy = z.infer<typeof AI_POLICY>;
export const DEFAULT_AI_POLICY: AiPolicy = { mode: "rules", providerFeatures: [...AI_FEATURES], redact: true, model: DEFAULT_AI_MODEL, monthlyTokenLimit: 0, mcpDailyLimit: 5000, dataPolicy: undefined };

// ------------------------------------------------------------------------------------------------ reply classification

/** What an inbound reply to outreach is. Product labels: each one has a defined follow-up, so they are not an agency list. */
export const REPLY_LABELS = ["interested", "meeting", "not_now", "not_interested", "wrong_person", "out_of_office", "unsubscribe", "bounce", "other"] as const;
export const REPLY_LABEL = z.enum(REPLY_LABELS);
export type ReplyLabel = z.infer<typeof REPLY_LABEL>;

export const CONFIDENCE = z.enum(["high", "medium", "low"]);
export type Confidence = z.infer<typeof CONFIDENCE>;

/** The label stored on an inbound message: by the rules, by the provider, or set by a person (`manual`). */
export const MESSAGE_CLASS = z.object({
  label: REPLY_LABEL,
  confidence: CONFIDENCE,
  engine: z.enum(["rules", "provider", "manual"]),
  at: ISO,
  /** The date an out-of-office or "not now" reply says the person is back, when it says one. */
  returnOn: ISO_DATE.optional(),
});
export type MessageClass = z.infer<typeof MESSAGE_CLASS>;

// ---------------------------------------------------------------------------------------------------------- results

export const ENGINE = z.enum(["rules", "provider"]);
export type Engine = z.infer<typeof ENGINE>;

/** Every AI answer says which engine wrote it, so the screen can say so plainly. `note` explains a fall back to the rules. */
export type AiResult<T> = { engine: Engine; model?: string; note?: string; at: string; data: T };

export const TONE = z.enum(["watch", "risk"]);
export const SENTIMENT = z.enum(["positive", "neutral", "negative", "unknown"]);
export type Sentiment = z.infer<typeof SENTIMENT>;

export const OPEN_ITEM_KINDS = ["task", "invoice", "deal", "ticket", "conversation", "proposal", "waiting"] as const;

/** A client summary (kind "summary") or pre-call brief (kind "brief", adds talking points and the latest messages). */
export const CLIENT_SUMMARY = z.object({
  kind: z.enum(["summary", "brief"]),
  clientId: z.string(),
  clientName: z.string(),
  headline: z.string(),
  sentiment: SENTIMENT,
  sentimentReason: z.string(),
  lastActivity: z.object({ at: ISO.nullable(), label: z.string() }),
  openItems: z.array(z.object({ kind: z.enum(OPEN_ITEM_KINDS), label: z.string(), detail: z.string().optional(), href: z.string().optional() })),
  risks: z.array(z.object({ tone: TONE, text: z.string(), evidence: z.string() })),
  nextSteps: z.array(z.string()),
  talkingPoints: z.array(z.string()).optional(),
  recentMessages: z.array(z.object({ at: ISO, direction: z.enum(["in", "out"]), from: z.string(), excerpt: z.string() })).optional(),
  /** Record kinds the caller's role cannot see in full; the summary leaves them out and says so. */
  hiddenFrom: z.array(z.string()),
});
export type ClientSummary = z.infer<typeof CLIENT_SUMMARY>;

export const REPLY_VERDICT = z.object({
  label: REPLY_LABEL,
  confidence: CONFIDENCE,
  reason: z.string(),
  action: z.enum(["book_meeting", "send_info", "pause_until", "stop_contact", "find_right_person", "none"]),
  returnOn: ISO_DATE.optional(),
  messageId: z.string().optional(),
  stored: z.boolean(),
});
export type ReplyVerdict = z.infer<typeof REPLY_VERDICT>;

export const REPLY_DRAFT = z.object({
  body: z.string(),
  subject: z.string().optional(),
  label: REPLY_LABEL.optional(),
  /** The saved reply the draft is built on, when one fits. */
  snippet: z.object({ id: z.string(), title: z.string() }).optional(),
  /** What the writer still has to check or fill in. */
  todo: z.array(z.string()),
});
export type ReplyDraft = z.infer<typeof REPLY_DRAFT>;

// ------------------------------------------------------------------------------------------------ AI reply agent (X21-OPS-24)

/** One reply category (a reply label): whether the agent drafts for it, and whether it may also send without a person. */
export const AI_REPLY_CATEGORY = z.strictObject({ id: REPLY_LABEL, enabled: z.boolean(), autoSend: z.boolean() });
/**
 * The reply agent (settings key aiReplyAgent). Drafts for enabled categories go to the review queue. Auto-send is off by
 * default and sends only when the classifier's confidence reaches confidenceFloor, the thread has no attachment, the client
 * is not tagged "always human", and the provider wrote the draft.
 */
export const AI_REPLY_AGENT = z.strictObject({
  categories: z
    .array(AI_REPLY_CATEGORY)
    .max(REPLY_LABELS.length)
    .refine((a) => new Set(a.map((c) => c.id)).size === a.length, "List each category once."),
  confidenceFloor: CONFIDENCE,
});
export type AiReplyAgent = z.infer<typeof AI_REPLY_AGENT>;
export const DEFAULT_AI_REPLY_AGENT: AiReplyAgent = {
  categories: REPLY_LABELS.map((id) => ({ id, enabled: false, autoSend: false })),
  confidenceFloor: "high",
};

export const PROPOSAL_DRAFT = z.object({
  title: z.string(),
  sections: z.array(z.object({ title: z.string(), kind: z.enum(["text", "pricing", "deposit", "terms"]), body: z.string() })),
  /** What the writer still has to add, because the deal does not say it. */
  gaps: z.array(z.string()),
  proposalId: z.string().optional(),
});
export type ProposalDraft = z.infer<typeof PROPOSAL_DRAFT>;

export const ACTION_ITEM = z.object({ text: z.string(), owner: z.string().optional(), due: ISO_DATE.optional() });
export type ActionItem = z.infer<typeof ACTION_ITEM>;

export const CALL_SUMMARY = z.object({
  summary: z.string(),
  keyPoints: z.array(z.string()),
  decisions: z.array(z.string()),
  actionItems: z.array(ACTION_ITEM),
  nextStep: z.string().optional(),
  /** The note activity written on the deal, contact or client timeline, when the summary was applied. */
  noteId: z.string().optional(),
});
export type CallSummary = z.infer<typeof CALL_SUMMARY>;

export const MEETING_NOTES = z.object({
  summary: z.string(),
  decisions: z.array(z.string()),
  actionItems: z.array(ACTION_ITEM.extend({ ownerId: z.string().optional() })),
  applied: z.boolean(),
});
export type MeetingNotes = z.infer<typeof MEETING_NOTES>;

/** The fields a pasted lead email states, each copied as written. A field the email does not state is left out. */
export const LEAD_PASTE_FIELDS = z.object({ name: z.string().optional(), company: z.string().optional(), need: z.string().optional(), budget: z.string().optional() });
export type LeadPasteFields = z.infer<typeof LEAD_PASTE_FIELDS>;

export const LEAD_ASSIST = z.object({
  subjectType: z.enum(["deal", "lead"]),
  subjectId: z.string(),
  score: z.number().int().min(0).max(100),
  band: z.enum(["hot", "warm", "cold"]),
  reasons: z.array(z.object({ text: z.string(), points: z.number().int() })),
  nextStep: z.string(),
  /** `fields` is set only when the request carried a pasted email: what the email states, `{}` when it states none. */
  draft: z.object({ subject: z.string(), body: z.string(), fields: LEAD_PASTE_FIELDS.optional() }),
});
export type LeadAssist = z.infer<typeof LEAD_ASSIST>;

/** The query a plain-language report question became, in the terms of the report engine (GET /metrics/query). */
export const REPORT_PLAN = z.object({
  metric: z.string(),
  from: ISO_DATE.optional(),
  to: ISO_DATE.optional(),
  by: z.enum(["client", "office", "service", "person", "department"]).optional(),
  series: z.boolean().optional(),
});
export type ReportPlan = z.infer<typeof REPORT_PLAN>;

export type ReportAnswer = {
  understood: boolean;
  question: string;
  plan: ReportPlan | null;
  /** The plan in words, shown with the answer: "Revenue, 1 Oct to 31 Oct, by client". */
  reading: string;
  /** The report engine's own answer to the plan (the body of GET /metrics/query), or null when nothing was run. */
  result: Record<string, unknown> | null;
  /** One sentence that answers the question. */
  sentence: string;
  /** What can be asked, when the question was not understood. */
  suggestions: { metric: string; label: string }[];
};

/** What the AI settings page needs in one read. */
export type AiStatus = {
  policy: AiPolicy;
  /** The engine a feature runs on right now, given the policy and the key: "rules" until a key exists and the policy allows the provider. */
  effectiveEngine: Engine | "off";
  provider: { available: boolean; source: "vault" | "env" | null; hint: string | null; model: string };
  usage: { calls: number; providerCalls: number; tokensIn: number; tokensOut: number; since: string };
  features: { id: AiFeature; providerAllowed: boolean; improvesWithKey: string }[];
  mcp: { path: string; scope: string; tools: string[]; callsToday: number };
};

export type AiCallRow = { at: string; feature: string; engine: string; model: string | null; outcome: string; tokensIn: number; tokensOut: number; subjectType: string; subjectId: string | null; by: string | null };

// Monthly client commentary (X7-03i): the rules write it from the month's records; the model may only reword it.
export const MONTH_COMMENTARY = z.object({ month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/), commentary: z.string(), highlights: z.array(z.string()) });
export type MonthCommentary = z.infer<typeof MONTH_COMMENTARY>;
